import { useAutomationActions } from '@mm/state/actions/automationActions';
import WidgetModel, { WidgetTypes } from '@mm/state/model/WidgetModel';
import { StoreState } from '@mm/state/store-interfaces';
import { Graph } from 'graphlib';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getWidgetPageMap } from './use-fetch-dialogue';
import { NodeTypes } from './NodeTypes';

export type GraphOpts = {
  campaignId: number;
  schedulerDateTimeVisible?: boolean;
  scheduledAt?: string;
  intervalInMin?: number;
};

const constants = {
  initial: 'initial',
  adder(id) {
    return `a-${id}`;
  },
  widget(id) {
    return `w-${id}`;
  },
  dialogue(id) {
    return `d-${id}`;
  },
  getId(name) {
    return name.toString().split('-')[1];
  },
};

const position = { x: 0, y: 0 };

const createNode = (id, type, data) => ({
  position,
  draggable: false,
  id: id.toString(),
  type,
  data,
});

const createEdge = (
  source,
  target,
  type = '',
  sourceHandle,
  data = {},
  ...rest
) => ({
  id: `${source}-${target}`,
  source: source.toString(),
  target: target.toString(),
  type,
  sourceHandle,
  data,
  ...rest,
});

export const widgetToNodeType = (w: WidgetModel): NodeTypes | undefined => {
  switch (w.type) {
    case WidgetTypes.DELAY:
      return NodeTypes.DELAY;
    case WidgetTypes.NAVIGATE: {
      if (w.config.subtype === 'unicorn_detector') {
        return NodeTypes.UNICORN_DETECTOR;
      }
      return w.config.oppositePageId ? NodeTypes.CONDITION : NodeTypes.DIALOGUE;
    }
    case WidgetTypes.EMAIL:
      return NodeTypes.DIALOGUE;
    case WidgetTypes.TAGS:
      return NodeTypes.TAG;
    case WidgetTypes.SEND_DATA: {
      const kind = w.config.kind || ('' as string);

      switch (kind) {
        case 'mailchimp':
          return NodeTypes.MAILCHIMP;
        case 'google_sheets':
          return NodeTypes.GOOGLE_SHEET;
        case 'facebook_ads_audience':
          return NodeTypes.FACEBOOK_ADS;
        case 'gohighlevel':
          return NodeTypes.GOHIGHLEVEL;
        case 'salesforce':
          return NodeTypes.SALESFORCE;
        case 'shopify':
          return NodeTypes.SHOPIFY;
        case 'klaviyo':
          return NodeTypes.KLAVIYO;
        case 'sendlane':
          return NodeTypes.SENDLANE;
        default: {
          return w.config.resourceSubscriptionTriggerName !== undefined
            ? NodeTypes.ZAPIER
            : NodeTypes.JSON_WEBHOOK;
        }
      }
    }
  }
};

const createWidgetNode = (widget: WidgetModel, opts) => {
  const type = widgetToNodeType(widget);

  if (!type) {
    return;
  }

  return createNode(widget.id, type, { widget, opts });
};

const createWidgetEdge = (
  parent: WidgetModel,
  child: WidgetModel,
  source,
  opts,
) => {
  let type = 'edgebutton';
  const data = {
    dialogueId: parent.pageId,
    sortOrder: parent.sortOrder + 1,
    ...opts,
  };

  // split nodes
  if (parent.type === WidgetTypes.NAVIGATE) {
    if (parent.config.oppositePageId) {
      const map = getWidgetPageMap(parent);
      type = '';
      if (source) {
        data.dialogueId = map[source];
        data.sortOrder = 1;
      }
    }
  }

  return createEdge(parent.id, child.id, type, source, data);
};

const createWidgetAdder = (widget: WidgetModel, opts: GraphOpts) =>
  createNode(`a-${widget.id}`, 'adder', {
    dialogueId: widget.pageId,
    sortOrder: widget.sortOrder + 1,
    ...opts,
  });

const allowed = [
  WidgetTypes.DELAY,
  WidgetTypes.NAVIGATE,
  WidgetTypes.SEND_DATA,
  WidgetTypes.TAGS,
  WidgetTypes.EMAIL,
];

type Node = ReturnType<typeof createNode>;
type Edge = ReturnType<typeof createEdge>;

export const useGraph = () => {
  const automationActions = useAutomationActions();

  const storeWidgets = useSelector(
    (state: StoreState) => state.widget.entities,
  );

  const widgets = storeWidgets.filter(w => allowed.includes(w.type));

  let nodes: Node[] = [];
  let edges: Edge[] = [];
  let adders: (Edge | Node)[] = [];

  let graphLibInstance = new Graph({ directed: true });

  const buildGraph = (
    did,
    parent: WidgetModel,
    source?: string,
    opts?: GraphOpts,
  ) => {
    console.debug(`get-graph: building ${did}`);
    const pageWidgets = widgets.filter(w => w.pageId === did);

    pageWidgets.forEach((widget, i, arr) => {
      const prev = arr[i - 1] || parent;

      const node = createWidgetNode(widget, opts);
      console.debug(`get-graph: set node ${node?.id}`);

      const edge =
        prev && createWidgetEdge(prev, widget, i === 0 && source, opts);
      prev && console.debug(`get-graph: set edge ${edge?.id}`);

      if (node) {
        nodes = [...nodes, node].filter(Boolean);
      }

      if (edge) {
        edges = [...edges, edge].filter(Boolean);
      }

      const map = getWidgetPageMap(widget);

      Object.entries(map).map(([name, id]) => buildGraph(id, widget, name));
    });
  };

  const attachStartNode = (dialogueId, opts) => {
    const startNode = createNode(constants.initial, 'scheduler', {
      widget: {
        id: constants.initial,
        pageId: dialogueId,
        // it would be better to identify sort initial order based on first visible or last hidden widget in a dialogue
        sortOrder: 0,
      },
      ...opts,
    });

    nodes = [...nodes, startNode];

    graphLibInstance.setNode(startNode.id, {
      ...startNode,
    });

    return startNode;
  };

  const attachEndNodes = (opts: GraphOpts) => {
    const sources = edges.map(e => e.source);

    const sinkNodes = nodes
      .filter(n => !sources.includes(n.id))
      .filter(
        n => !['condition', 'unicornDetector'].find(type => type === n.type),
      ); // handled internally

    sinkNodes.forEach(node => {
      const adder = createWidgetAdder(node.data.widget, opts);
      const edge = createEdge(node.id, adder.id, 'straight', '', opts);

      adders = [...adders, adder, edge];
    });
  };

  // Note: Fixes order of Condition and Unicorn detector elements.
  // It feels hacky to me, and we probably want to come up with a better
  // way to store order in BE. Currently sortOrder is a mess.
  const resortEdges = () => {
    const groupedBySource = edges.reduce((acc, cur, idx) => {
      if (acc[cur.source]) {
        return {
          ...acc,
          [cur.source]:
            cur.sourceHandle === 'yes'
              ? [cur, ...acc[cur.source]]
              : cur.sourceHandle === 'no'
              ? [...acc[cur.source], cur]
              : [cur, ...acc[cur.source]],
        };
      }

      return {
        ...acc,
        [cur.source]: [cur],
      };
    }, {});
    const sortedEdges: Edge[] = [];
    Object.values(groupedBySource).forEach((edges: Edge[]) => {
      sortedEdges.push(...edges);
    });
    edges = sortedEdges;
  };

  const addDataToGraphLib = () => {
    nodes.forEach(node =>
      graphLibInstance.setNode(String(node.id), {
        ...node,
      }),
    );

    edges.forEach(edge =>
      graphLibInstance.setEdge(String(edge.source), String(edge.target), {
        ...edge,
      }),
    );
  };

  const init = (dialogueId: number, opts: GraphOpts) => {
    nodes = [];
    edges = [];
    adders = [];
    graphLibInstance = new Graph({ directed: true, multigraph: true });
    graphLibInstance.setGraph(String(dialogueId));

    // Note: the datastructure with nodes, edges and adders
    // is not powerful enough for us to answer some of the
    // questions we need, e.g. for UnicornDetector,
    // since it does not make it easy to traverse the graph.
    // That is why I have added GraphLib library that we already
    // have into the mix.
    // ideally we can modify how we create element for the graph,
    // with the library providing easy ways to find sources, sink nodes,
    // etc. But currently I do not have time to fully refactor.
    const startNode = attachStartNode(dialogueId, opts);

    buildGraph(dialogueId, startNode.data.widget, undefined, opts);
    addDataToGraphLib();
    attachEndNodes(opts);
    resortEdges();

    automationActions.setGraphLibInstance(graphLibInstance);

    return [...nodes, ...edges, ...adders];
  };

  useEffect(() => {
    return () => {
      automationActions.resetActionsState();
    };
  }, []);

  return {
    init,
  };
};
